/**
 * As peças comuns aos três cartões de Integrações (história 11.2, AD-30, FR-82): como o estado de
 * uma credencial aparece, e o formulário de troca de um segredo.
 *
 * Elas moram num módulo da **tela**, e não de um serviço, porque são desenho e nada mais: o dado
 * vem dos dois donos (`comunicacao` e `integracao-erp`) pelos hooks de cada um, e os dois
 * `EstadoDaCredencial` gerados têm a mesma forma — é essa forma, e só ela, que este arquivo lê.
 *
 * **O segredo nunca aparece inteiro, e nunca é pré-preenchido.** O serviço devolve no máximo os
 * últimos caracteres; o campo de troca é `type="password"`, nasce vazio e esvazia depois de gravar.
 * Um campo que viesse preenchido com "••••1234" convidaria alguém a salvar o mascaramento como se
 * fosse a credencial.
 *
 * **A mensagem do serviço vai à tela como veio** — o `422` do valor vazio, o `403` de quem perdeu a
 * permissão, o `503` da chave de cifra ausente. A única recusa local é a do campo vazio, que
 * poupa uma ida à rede para ouvir o que já se sabe.
 */

import { type FormEvent, useId, useState } from 'react'

import { ErroDaApi, MENSAGEM_DE_ERRO_DESCONHECIDO } from '@/api/erros'
import './integracoes.css'

/** A forma comum dos dois `EstadoDaCredencial` gerados. */
export interface EstadoDeUmaCredencial {
  configurada: boolean
  ultimos_caracteres?: string | null
  atualizada_em?: string | null
}

export const NAO_CONFIGURADA = 'Não configurado'
export const CONFIGURADA = 'Configurado'
export const FALTA_O_VALOR = 'Informe o novo valor.'
export const CREDENCIAL_TROCADA = 'Credencial trocada.'

/** A recusa como ela veio do serviço, ou a genérica quando não é `ErroDaApi`. */
export function mensagemDe(falha: unknown): string {
  return falha instanceof ErroDaApi ? falha.message : MENSAGEM_DE_ERRO_DESCONHECIDO
}

/** A data em formato brasileiro, no fuso de São Paulo. */
export function dataBrasileira(instante: string): string {
  return new Date(instante).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })
}

/**
 * "Configurado · final ••••1234 · atualizado em 24/09/2026", ou "Não configurado".
 *
 * Os números vão em `.numero` (UX-DR1). O selo carrega `data-situacao`, que o CSS pinta.
 */
export function EstadoDaCredencialNaTela({ estado }: { estado: EstadoDeUmaCredencial }) {
  if (!estado.configurada) {
    return (
      <p className="integracoes__estado">
        <span className="integracoes__selo" data-situacao="ausente">
          {NAO_CONFIGURADA}
        </span>
      </p>
    )
  }
  const final = estado.ultimos_caracteres ?? ''
  return (
    <p className="integracoes__estado">
      <span className="integracoes__selo" data-situacao="configurada">
        {CONFIGURADA}
      </span>
      {final === '' ? null : (
        <>
          {' · final '}
          <span className="numero" aria-label={`final ${final}`}>
            ••••{final}
          </span>
        </>
      )}
      {estado.atualizada_em ? (
        <>
          {' · atualizado em '}
          <span className="numero">{dataBrasileira(estado.atualizada_em)}</span>
        </>
      ) : null}
    </p>
  )
}

/** O estado de uma mutação, como o formulário de troca o lê. */
export interface EstadoDaGravacao {
  isPending: boolean
  isError: boolean
  isSuccess: boolean
  error: unknown
  reset: () => void
}

export const MOSTRAR_O_VALOR = 'Mostrar o valor'
export const OCULTAR_O_VALOR = 'Ocultar o valor'

/**
 * O olho do campo de segredo (protótipo v2, `👁` ao lado do token de administrador), em SVG e não
 * em emoji pela mesma razão do ícone de copiar: o glifo depende da fonte instalada. Decorativo —
 * o nome acessível está no botão.
 */
function IconeDeOlho({ aberto }: { aberto: boolean }) {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
      <path
        d="M1.5 8s2.5-4.5 6.5-4.5S14.5 8 14.5 8s-2.5 4.5-6.5 4.5S1.5 8 1.5 8z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
      />
      <circle cx="8" cy="8" r="2" fill="none" stroke="currentColor" strokeWidth="1.4" />
      {aberto ? null : (
        <path d="M3 13L13 3" fill="none" stroke="currentColor" strokeWidth="1.4" />
      )}
    </svg>
  )
}

/**
 * O formulário de troca de **um** segredo: um campo de senha e um botão.
 *
 * O campo esvazia quando a troca dá certo — o valor não fica na memória da tela depois de gravado.
 *
 * `comOlho` põe o botão de mostrar/ocultar do protótipo v2 (o token de administrador do uazapi):
 * ele só alterna o `type` do campo **enquanto se digita** — o valor gravado nunca volta do serviço
 * para ser mostrado, e o campo volta a esconder depois de gravar.
 */
export function TrocaDeSegredo({
  rotulo,
  ajuda,
  comOlho = false,
  gravacao,
  aoTrocar,
}: {
  rotulo: string
  /** A frase de apoio abaixo do campo (o protótipo a tem no token de administrador). */
  ajuda?: string
  comOlho?: boolean
  gravacao: EstadoDaGravacao
  /** Chama a mutação; `aoGravar` limpa o campo quando ela dá certo. */
  aoTrocar: (valor: string, aoGravar: () => void) => void
}) {
  const id = useId()
  const [valor, setValor] = useState('')
  const [visivel, setVisivel] = useState(false)
  const [recusaLocal, setRecusaLocal] = useState<string | null>(null)

  function enviar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    gravacao.reset()
    if (valor.trim() === '') {
      setRecusaLocal(FALTA_O_VALOR)
      return
    }
    setRecusaLocal(null)
    aoTrocar(valor, () => {
      setValor('')
      setVisivel(false)
    })
  }

  return (
    <form className="integracoes__formulario" onSubmit={enviar} noValidate>
      <label className="integracoes__rotulo" htmlFor={id}>
        {rotulo}
      </label>
      <div className="integracoes__linha">
        <input
          id={id}
          className="integracoes__campo"
          type={comOlho && visivel ? 'text' : 'password'}
          autoComplete="new-password"
          value={valor}
          disabled={gravacao.isPending}
          onChange={(evento) => {
            setRecusaLocal(null)
            gravacao.reset()
            setValor(evento.target.value)
          }}
        />
        {comOlho ? (
          <button
            className="integracoes__olho"
            type="button"
            aria-pressed={visivel}
            aria-label={visivel ? OCULTAR_O_VALOR : MOSTRAR_O_VALOR}
            title={visivel ? OCULTAR_O_VALOR : MOSTRAR_O_VALOR}
            onClick={() => {
              setVisivel((atual) => !atual)
            }}
          >
            <IconeDeOlho aberto={!visivel} />
          </button>
        ) : null}
        <button className="integracoes__botao" type="submit" disabled={gravacao.isPending}>
          {gravacao.isPending ? 'Salvando…' : 'Salvar'}
        </button>
      </div>
      {ajuda === undefined ? null : <p className="integracoes__ajuda">{ajuda}</p>}
      {recusaLocal === null ? null : (
        <p className="integracoes__erro" role="alert">
          {recusaLocal}
        </p>
      )}
      {gravacao.isError ? (
        <p className="integracoes__erro" role="alert">
          {mensagemDe(gravacao.error)}
        </p>
      ) : null}
      {gravacao.isSuccess ? (
        <p className="integracoes__sucesso" role="status">
          {CREDENCIAL_TROCADA}
        </p>
      ) : null}
    </form>
  )
}

/** A leitura das credenciais falhou: a mensagem do serviço e "Tentar de novo" (State Patterns). */
export function ErroDaLeitura({ erro, aoTentar }: { erro: unknown; aoTentar: () => void }) {
  return (
    <p className="integracoes__erro" role="alert">
      {mensagemDe(erro)}{' '}
      <button type="button" className="integracoes__tentar" onClick={aoTentar}>
        Tentar de novo
      </button>
    </p>
  )
}

/** A frase de carregando da seção de credenciais. */
export const CARREGANDO_AS_CREDENCIAIS = 'Carregando as credenciais…'

/** A resposta veio sem o bloco de credenciais para quem tem a permissão — o serviço decidiu. */
export const SEM_CREDENCIAIS_PARA_ESTE_PERFIL =
  'As credenciais não estão disponíveis para o seu perfil.'
